import { storageService } from './storageService';
import { ScoringWeights, DEFAULT_WEIGHTS } from './scoringService';

export interface FeedbackInsights {
  bangedCount: number;
  floppedCount: number;
  weightAdjustments: Partial<ScoringWeights>;
  promptHints: string[];
}

const AXIS_NAMES_EN: Record<string, string> = {
  freshness: 'originality and freshness',
  specificity: 'concrete details (names, numbers, moments)',
  personaFit: 'authentic voice consistency',
  replyHook: 'reply-worthy hooks and questions',
  readability: 'clean readable structure',
};

const AXIS_NAMES_JA: Record<string, string> = {
  freshness: '新規性・独自性',
  specificity: '具体性（固有名詞・数字・体験）',
  personaFit: '文体の一貫性',
  replyHook: '返信したくなるフック',
  readability: '読みやすさ',
};

export const feedbackService = {
  /**
   * 「伸びた」「滑った」ツイートのスコア傾向を分析し、
   * 重み調整とプロンプトヒントを返す
   */
  analyze(): FeedbackInsights {
    const banged = storageService.getFeedbackItems('banged');
    const flopped = storageService.getFeedbackItems('flopped');

    if (banged.length === 0) {
      return { bangedCount: 0, floppedCount: flopped.length, weightAdjustments: {}, promptHints: [] };
    }

    // 各軸の平均スコアを計算
    const axes: (keyof ScoringWeights)[] = ['freshness', 'specificity', 'personaFit', 'replyHook', 'readability', 'risk'];

    const bangedAvg = this.computeAxisAverages(banged, axes);
    const floppedAvg = flopped.length >= 2 ? this.computeAxisAverages(flopped, axes) : null;

    // 重み調整: 伸びたツイートで高い軸を上げ、低い軸を下げる
    const adjustments: Partial<ScoringWeights> = {};
    const strongAxes: string[] = [];
    const weakAxes: string[] = [];

    for (const axis of axes) {
      if (axis === 'risk') continue; // リスクは調整対象外
      const avg = bangedAvg[axis];
      if (avg === undefined) continue;

      if (avg >= 0.7) {
        // この軸が高いツイートが伸びてる → 重みを少し上げる
        adjustments[axis] = Math.min(0.35, DEFAULT_WEIGHTS[axis] + 0.05);
        strongAxes.push(axis);
      } else if (avg <= 0.4) {
        weakAxes.push(axis);
      }

      // floppedとの差分がある場合、さらに調整
      if (floppedAvg && floppedAvg[axis] !== undefined) {
        const diff = avg - floppedAvg[axis]!;
        if (diff > 0.15) {
          // 伸びた方が明らかに高い軸 → 重み上げ
          adjustments[axis] = Math.min(0.35, DEFAULT_WEIGHTS[axis] + 0.08);
        }
      }
    }

    // プロンプトヒント生成
    const locale = localStorage.getItem('bangerfy_locale') === 'ja' ? 'ja' : 'en';
    const axisNames = locale === 'ja' ? AXIS_NAMES_JA : AXIS_NAMES_EN;
    const hints: string[] = [];

    if (strongAxes.length > 0) {
      const names = strongAxes.map(a => axisNames[a]).filter(Boolean);
      hints.push(
        locale === 'ja'
          ? `このユーザーのツイートは${names.join('・')}が高い時に伸びる傾向がある`
          : `This user's tweets tend to perform well when they have strong ${names.join(', ')}`
      );
    }

    // 伸びたツイートの長さ傾向
    if (banged.length >= 3) {
      const avgLength = banged.reduce((s, i) => s + i.content.length, 0) / banged.length;
      if (avgLength < 100) {
        hints.push(locale === 'ja' ? '短いツイート（100字以下）が伸びやすい' : 'Shorter tweets (under 100 chars) tend to perform better');
      } else if (avgLength > 200) {
        hints.push(locale === 'ja' ? '長めのツイート（200字以上）が伸びやすい' : 'Longer tweets (200+ chars) tend to perform better');
      }

      // 疑問形の傾向
      const questionRate = banged.filter(i => i.content.includes('?') || i.content.includes('？')).length / banged.length;
      if (questionRate > 0.5) {
        hints.push(locale === 'ja' ? '質問形式のツイートが伸びやすい' : 'Question-style tweets tend to perform better');
      }

      // 改行の傾向
      const lineBreakRate = banged.filter(i => i.content.includes('\n')).length / banged.length;
      if (lineBreakRate > 0.6) {
        hints.push(locale === 'ja' ? '改行を使った構造化ツイートが伸びやすい' : 'Structured tweets with line breaks tend to perform better');
      }
    }

    // 伸びたツイートのコンテンツパターン（最大5件をプロンプト例として）
    const topBanged = banged
      .filter(i => (i.engagementLikes ?? 0) > 0)
      .sort((a, b) => (b.engagementLikes ?? 0) - (a.engagementLikes ?? 0))
      .slice(0, 5);

    if (topBanged.length > 0) {
      const examples = topBanged.map(i =>
        `"${i.content.slice(0, 200)}" (${i.engagementLikes ?? '?'} likes)`
      );
      hints.push(
        locale === 'ja'
          ? `実績のある伸びたツイート例:\n${examples.join('\n')}`
          : `Proven high-performing tweets:\n${examples.join('\n')}`
      );
    }

    return {
      bangedCount: banged.length,
      floppedCount: flopped.length,
      weightAdjustments: adjustments,
      promptHints: hints,
    };
  },

  /**
   * 各軸の平均スコアを計算
   */
  computeAxisAverages(
    items: { score?: number; metadata?: Record<string, any> }[],
    axes: string[]
  ): Record<string, number> {
    const sums: Record<string, number> = {};
    const counts: Record<string, number> = {};

    for (const item of items) {
      const breakdown = item.metadata?.breakdown;
      if (!breakdown) continue;

      for (const axis of axes) {
        if (typeof breakdown[axis] === 'number') {
          sums[axis] = (sums[axis] || 0) + breakdown[axis];
          counts[axis] = (counts[axis] || 0) + 1;
        }
      }
    }

    const avg: Record<string, number> = {};
    for (const axis of axes) {
      if (counts[axis]) avg[axis] = sums[axis] / counts[axis];
    }
    return avg;
  },

  /**
   * フィードバックに基づいて重みを自動調整して保存
   */
  applyWeightAdjustments(): ScoringWeights {
    const { weightAdjustments } = this.analyze();
    const current = JSON.parse(localStorage.getItem('scoring_weights') || 'null') || { ...DEFAULT_WEIGHTS };

    const adjusted = { ...current, ...weightAdjustments };

    // 正規化: 正の重みの合計を ~1.0 に
    const positiveSum = Object.entries(adjusted)
      .filter(([k]) => k !== 'risk')
      .reduce((s, [, v]) => s + Math.abs(v as number), 0);

    if (positiveSum > 0) {
      for (const key of Object.keys(adjusted) as (keyof ScoringWeights)[]) {
        if (key !== 'risk') {
          adjusted[key] = (adjusted[key] / positiveSum) * 0.95; // 95% for positive, 5% for risk
        }
      }
      adjusted.risk = DEFAULT_WEIGHTS.risk;
    }

    localStorage.setItem('scoring_weights', JSON.stringify(adjusted));
    return adjusted;
  },
};
